
"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { toast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useLanguage } from "@/context/LanguageContext";
import { useState } from "react";
import { Loader2 } from "lucide-react";

const ChangePasswordForm = () => {
  const { language } = useLanguage();
  const [isLoading, setIsLoading] = useState(false);
  const [showResetLink, setShowResetLink] = useState(false);

  const formSchema = z
    .object({
      oldPassword: z.string().min(1, { 
        message: language === 'en' ? "Old password is required" : "請輸入舊密碼" 
      }),
      newPassword: z.string().min(6, { 
        message: language === 'en' ? "New password must be at least 6 characters" : "新密碼至少需要6個字符" 
      }),
      confirmNewPassword: z.string().min(1, { 
        message: language === 'en' ? "Please confirm your new password" : "請確認新密碼" 
      }),
    })
    .refine((data) => data.newPassword === data.confirmNewPassword, {
      message: language === 'en' ? "Passwords do not match" : "密碼不匹配",
      path: ["confirmNewPassword"],
    });

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      oldPassword: "",
      newPassword: "",
      confirmNewPassword: "",
    },
  });

  const handleForgotPassword = async () => {
    setIsLoading(true);
    setShowResetLink(false);
    try {
      const {
        data: { user },
        error: getUserError,
      } = await supabase.auth.getUser();

      if (getUserError || !user) {
        toast({
          variant: "destructive",
          title: language === 'en' ? "Error" : "錯誤",
          description: language === 'en' ? "Could not get user email" : "無法獲取用戶郵箱",
        });
        setIsLoading(false);
        return;
      }

      const { error: resetError } = await supabase.auth.resetPasswordForEmail(
        user.email!,
        {
          redirectTo: window.location.origin + "/reset-password",
        }
      );

      if (resetError) {
        toast({
          variant: "destructive",
          title: language === 'en' ? "Error" : "錯誤",
          description: resetError.message,
        });
      } else {
        toast({
          title: language === 'en' ? "Password reset email sent" : "密碼重置郵件已發送",
          description: language === 'en' ? "Please check your email for password reset instructions" : "請檢查您的郵箱以獲取密碼重置說明",
        });
      }
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: language === 'en' ? "Error" : "錯誤",
        description: error.message || (language === 'en' ? "An unexpected error occurred" : "發生意外錯誤"),
      });
    } finally {
      setIsLoading(false);
    }
  };

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    setIsLoading(true);
    setShowResetLink(false);

    try {
      const {
        data: { user: currentUser },
        error: getUserError,
      } = await supabase.auth.getUser();

      if (getUserError || !currentUser || !currentUser.email) {
        toast({
          variant: "destructive",
          title: language === 'en' ? "Error" : "錯誤",
          description: language === 'en' ? "Could not get user email" : "無法獲取用戶郵箱",
        });
        setIsLoading(false);
        return;
      }

      // Verify old password
      const { error: signInError, data: signInData } =
        await supabase.auth.signInWithPassword({
          email: currentUser.email,
          password: values.oldPassword,
        });

      if (signInError) {
        if (signInError.message === "Invalid login credentials") {
          toast({
            variant: "destructive",
            title: language === 'en' ? "Error" : "錯誤",
            description: language === 'en' ? "Old password is incorrect" : "舊密碼不正確",
          });
          setShowResetLink(true);
        } else {
          toast({
            variant: "destructive",
            title: language === 'en' ? "Error" : "錯誤",
            description: signInError.message || (language === 'en' ? "An unexpected error occurred" : "發生意外錯誤"),
          });
        }
        setIsLoading(false);
        return;
      }

      const { error: updateError } = await supabase.auth.updateUser({
        password: values.newPassword,
      });

      if (updateError) {
        toast({
          variant: "destructive",
          title: language === 'en' ? "Error" : "錯誤",
          description: updateError.message || (language === 'en' ? "An unexpected error occurred" : "發生意外錯誤"),
        });
      } else {
        toast({
          title: language === 'en' ? "Success" : "成功",
          description: language === 'en' ? "Password updated successfully" : "密碼更新成功",
        });
        form.reset();
      }
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: language === 'en' ? "Error" : "錯誤",
        description: error.message || (language === 'en' ? "An unexpected error occurred" : "發生意外錯誤"),
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
        <FormField
          control={form.control}
          name="oldPassword"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{language === 'en' ? 'Old Password' : '舊密碼'}</FormLabel>
              <FormControl>
                <Input type="password" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="newPassword"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{language === 'en' ? 'New Password' : '新密碼'}</FormLabel>
              <FormControl>
                <Input type="password" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="confirmNewPassword"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{language === 'en' ? 'Confirm New Password' : '確認新密碼'}</FormLabel>
              <FormControl>
                <Input type="password" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <Button type="submit" disabled={isLoading}>
          {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          {language === 'en' ? 'Change Password' : '修改密碼'}
        </Button>

        {showResetLink && (
          <div className="mt-4 text-sm">
            <Button
              variant="link"
              onClick={handleForgotPassword}
              disabled={isLoading}
              className="p-0 h-auto"
            >
              {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {language === 'en' ? 'Forgot your password? Send reset email' : '忘記密碼？發送重置郵件'}
            </Button>
          </div>
        )}
      </form>
    </Form>
  );
};

export default ChangePasswordForm;
